import { useCallback, useEffect, useState } from 'react';

/**
 * Routes: `/` (public decks), `/decks/:id` (editor), `/cards` (the shared library), `/users` and
 * `/users/:id`, `/revenue`, `/finance`, `/emails`, `/soon/:section` (placeholders).
 */
export type Route =
  | { name: 'decks' }
  | { name: 'deck'; id: string }
  | { name: 'cards' }
  | { name: 'users' }
  | { name: 'user'; id: string }
  | { name: 'revenue' }
  | { name: 'finance' }
  | { name: 'emails' }
  | { name: 'soon'; section: string };

export function parseRoute(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'decks' && parts[1]) return { name: 'deck', id: decodeURIComponent(parts[1]) };
  if (parts[0] === 'cards') return { name: 'cards' };
  if (parts[0] === 'users') {
    return parts[1] ? { name: 'user', id: decodeURIComponent(parts[1]) } : { name: 'users' };
  }
  if (parts[0] === 'revenue') return { name: 'revenue' };
  if (parts[0] === 'finance') return { name: 'finance' };
  if (parts[0] === 'emails') return { name: 'emails' };
  if (parts[0] === 'soon' && parts[1]) return { name: 'soon', section: parts[1] };
  return { name: 'decks' };
}

export function routePath(route: Route): string {
  switch (route.name) {
    case 'deck':
      return `/decks/${encodeURIComponent(route.id)}`;
    case 'cards':
      return '/cards';
    case 'users':
      return '/users';
    case 'user':
      return `/users/${encodeURIComponent(route.id)}`;
    case 'revenue':
      return '/revenue';
    case 'finance':
      return '/finance';
    case 'emails':
      return '/emails';
    case 'soon':
      return `/soon/${route.section}`;
    default:
      return '/';
  }
}

/** A minimal History API router: enough for a few routes without another dependency. */
export function useRoute(): [Route, (route: Route) => void] {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((next: Route) => {
    const path = routePath(next);
    if (path !== window.location.pathname) window.history.pushState(null, '', path);
    setRoute(next);
  }, []);

  return [route, navigate];
}
